import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get_bulk_data } from '../../reduxData/user/userAction';
import CustomPagination from '../../components/CustomPagination';
import EmptyData from '../../components/EmptyData';
import { Input, Button } from "@mui/joy";
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../hooks/formatDateTime';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";

const BulkUploaded = () => {
    const { t } = useTranslation();
    const { bulkData, totalBulkData } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Fetch data with filters
    const fetchData = async (page = 1, rows = 10) => {
        const params = {};

        params.search = searchTerm.trim();
        if (startDate) {
            params.startDate = dayjs(startDate).format("YYYY-MM-DD");
        }
        if (endDate) {
            params.endDate = dayjs(endDate).format("YYYY-MM-DD");
        }

        await get_bulk_data(dispatch, page, rows, params);
    };

    useEffect(() => {
        fetchData(currentPage, perPage);
    }, [searchTerm, startDate, endDate, currentPage, perPage]);

    const handlePageChange = (page, rows) => {
        setCurrentPage(page);
        setPerPage(rows);
    };

    const clearFilters = () => {
        setSearchTerm("");
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    const datePickerTextFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#fff',
            height: '46px',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e8671e',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#e8671e',
        },
        '& .MuiSvgIcon-root': {
            color: '#e8671e',
        },
    };

    const getTotalRows = (item) => {
        if (!item.total_rows) return 0;
        return typeof item.total_rows === 'object'
            ? item.total_rows.seconds || 0
            : item.total_rows;
    };

    const handleViewDetail = (item) => {
        navigate(`/bulk_upload/${item.id}`, { state: { id: item.id } });
    };

    const isImportCancelled = (item) => item?.status === "IMPORT_CANCELLED";

    return (
        <div className="container">
            <div className="audit-page-header">
                <div>
                    <h2>Bulk Uploads</h2>
                    <p>View all your bulk product upload history</p>
                </div>
            </div>

            {/* Filters - Updated with Start & End Date */}
            <div className="audit-filter-card">
                <div className="row g-3 d-flex align-items-end">
                    <div className="col-xl-4 col-lg-4">
                        <label className="audit-label">Search</label>
                        <Input
                            placeholder="Search by User..."
                            value={searchTerm}
                            style={{ borderRadius: '12px' }}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="col-xl-3 col-lg-3">
                            <label className="audit-label">From</label>
                            <DatePicker
                                value={startDate}
                                maxDate={dayjs()}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    if (endDate && newValue && dayjs(endDate).isBefore(newValue, "day")) {
                                        setEndDate(null);
                                    }
                                    setCurrentPage(1);
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        placeholder: "Start date",
                                        sx: datePickerTextFieldSx,
                                    },
                                }}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-3">
                            <label className="audit-label">To</label>
                            <DatePicker
                                value={endDate}
                                minDate={startDate || undefined}
                                maxDate={dayjs()}
                                onChange={(newValue) => {
                                    setEndDate(newValue);
                                    setCurrentPage(1);
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        placeholder: "End date",
                                        sx: datePickerTextFieldSx,
                                    },
                                }}
                            />
                        </div>
                    </LocalizationProvider>
                    <div className="col-xl-2 col-lg-2">
                        <button className="btn audit-clear-btn" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                </div>

            </div>

            {/* Table - Restored all columns */}
            <div className="table-responsive">
                <table className="table table-hover user-management-table">
                    <colgroup>
                        <col className="created_at" />
                        <col className="total_rows" />
                        <col className="total_rows" />
                        <col className="total_rows" />
                        <col className="success_row" />
                        <col className="rejected_row" />
                        <col className="product_created" />
                        <col className="product_updated" />
                        <col className="status" />
                        <col className="action" />
                    </colgroup>
                    <thead className="audit-table">
                        <tr>
                            <th className="text-uppercase">Created Date</th>
                            <th className="text-uppercase text-center">User</th>
                            <th className="text-uppercase text-center">Store Name</th>
                            <th className="text-uppercase text-center">Total Rows</th>
                            <th className="text-uppercase text-center">Success Rows</th>
                            <th className="text-uppercase text-center">Rejected Rows</th>
                            <th className="text-uppercase text-center">Product Created</th>
                            <th className="text-uppercase text-center">Product Updated</th>
                            <th className="text-uppercase text-center">Status</th>
                            <th className="text-uppercase text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(bulkData) && bulkData.length > 0 ? (
                            bulkData.map((item, index) => (
                                <tr key={item.id || index}>
                                    <td className="text-center">
                                        <strong>
                                            {formatDateTime(item.created_at)}
                                        </strong>
                                    </td>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">
                                                {item.user_name?.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="user-name">
                                                    {item.user_name}
                                                </div>
                                                <div className="user-email">
                                                    {item.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center">{item.store_name}</td>
                                    <td className="text-center"><strong>{getTotalRows(item)}</strong></td>
                                    <td className="text-center">
                                        <span className="status-badge success">
                                            {item.success_rows || 0}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <span className="status-badge failed">
                                            {item.rejected_rows || 0}
                                        </span>
                                    </td>
                                    <td className="text-center"><strong>{item.product_created || 0}</strong></td>
                                    <td className="text-center"><strong>{item.product_updated || 0}</strong></td>
                                    <td className="text-center">
                                        <span className={`status-badge ${isImportCancelled(item) ? "cancelled" : "approved"}`}>
                                            {isImportCancelled(item) ? "Cancelled" : "Completed"}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleViewDetail(item)}
                                        >
                                            View Detail
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center py-5">
                                    <EmptyData />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-4">
                {totalBulkData > 0 && (
                    <CustomPagination
                        total={totalBulkData}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default BulkUploaded;
